import { useCallback } from 'react'
import { useApolloClient, useMutation } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { useLocation } from 'wouter'
import { useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'
import { GoodieFile, isNewFile, useUploadFile } from '@/features/files'
import { useAuth } from '@/features/auth'

interface CreateOrganizationProps {
  name: string
  slug: string
  timezone: string
  currency: GraphQL.Currency
  brandingColor: string
  file: GoodieFile
  acceptedContract: boolean
  defaultLocale: GraphQL.AppLocale
}

type CreateOrganizationFn = (props: CreateOrganizationProps) => Promise<void>

export function useCreateOrganization() {
  const { t } = useLingui()

  const client = useApolloClient()
  const { refresh, switchOrg } = useAuth()

  const [, navigate] = useLocation()
  const [upload] = useUploadFile()
  const [createOrg] = useMutation(AdminAPI.organization.CREATE)
  const [createBranding] = useMutation(AdminAPI.branding.CREATE_BRANDING)

  const { handlePromiseCatch, errors } = Form.useErrorHelpers<FormData>({
    mapper(field, message) {
      if (field === 'slug' && message === 'has already been taken') {
        return [
          field,
          {
            type: message,
            message: t`This slug is already used by someone else.`
          }
        ]
      }
    }
  })

  const process = useCallback<CreateOrganizationFn>(
    async props => {
      const {
        name,
        slug,
        acceptedContract,
        timezone,
        currency,
        brandingColor,
        defaultLocale,
        file
      } = props

      if (!isNewFile(file)) return

      return (
        createOrg({
          variables: {
            input: {
              name,
              slug,
              acceptedContract
            }
          }
        })
          // We need to set the new organization on the session so that we can act inside of this organization
          .then(({ data, error }) => {
            if (error) return Promise.reject(error)
            if (!data) return Promise.reject('unknown')

            const org = getFragment(
              SharedAPI.organization.ORGANIZATION_FRAGMENT,
              data.createOrganization
            )

            return switchOrg(org.slug)
          })
          .then(() => upload(file))
          .then(file => {
            return createBranding({
              variables: {
                input: {
                  logoId: file.id,
                  brandColor: brandingColor,
                  timezone,
                  currency,
                  defaultLocale
                }
              }
            })
          })
          .then(() => {
            // We don't support multi tenancy caching currently, so let's just dump it all
            client.resetStore()
          })
          .then(() => refresh())
          .then(() => navigate('/goodies'))
          .catch(error =>
            handlePromiseCatch(error, {
              unknownString: t`Error creating organization, please try again, or reach out to our support`
            })
          )
      )
    },
    [
      createOrg,
      createBranding,
      upload,
      switchOrg,
      refresh,
      navigate,
      handlePromiseCatch,
      client,
      t
    ]
  )

  return {
    create: process,
    errors
  }
}
